<script setup lang="ts">
  import { computed, onBeforeUnmount, ref } from 'vue';
  import { type SliderModelValue, HandleKind } from './Slider.types';

  export interface SliderProps {
    modelValue: SliderModelValue;
    min?: number;
    max?: number;
    range?: boolean;
    disabled?: boolean;
  }

  interface SliderEmits {
    (e: 'update:modelValue', data: SliderModelValue): void;
  }

  const emit = defineEmits<SliderEmits>();

  const props = withDefaults(defineProps<SliderProps>(), {
    min: 0,
    max: 100,
    range: false,
    disabled: false,
  });

  const HANDLE_SIZE = 34;
  const HANDLE_RADIUS = HANDLE_SIZE / 2;

  const trackRef = ref<HTMLElement | null>(null);
  const draggingHandle = ref<HandleKind | null>(null);
  const topHandle = ref<HandleKind>(HandleKind.Upper);

  const clamp = (value: number) => Math.min(Math.max(value, props.min), props.max);

  const steps = computed(() => props.max - props.min);

  // Границы ручек. Входящие значения клемпятся в [min, max].
  // В одиночном режиме нижняя граница зафиксирована в props.min.
  const bounds = computed<{ lower: number; upper: number }>(() => {
    if (props.range) {
      return {
        lower: clamp(props.modelValue.min),
        upper: clamp(props.modelValue.max),
      };
    }

    return {
      lower: props.min,
      upper: clamp(props.modelValue.max),
    };
  });

  // Позиция значения на шкале в процентах (0–100). Деление считается от кол-ва шагов,
  // сама шкала уже поджата под диаметры ручек через CSS
  const toPercent = (value: number) => (steps.value > 0 ? ((value - props.min) / steps.value) * 100 : 0);

  const fillStyle = computed(() => {
    const left = props.range ? toPercent(bounds.value.lower) : 0;
    const right = toPercent(bounds.value.upper);

    return {
      left: `${left}%`,
      width: `${Math.max(right - left, 0)}%`,
    };
  });

  // Наружный сдвиг ручки: в двойном режиме левая на -радиус, правая на +радиус,
  // чтобы при равных значениях кружки стыковались кромка в кромку без нахлёста.
  const getHandleOffsetPx = (kind: HandleKind) => {
    if (!props.range) return 0;

    return kind === HandleKind.Lower ? -HANDLE_RADIUS : HANDLE_RADIUS;
  };

  const getHandleStyle = (kind: HandleKind) => ({
    left: `${toPercent(kind === HandleKind.Lower ? bounds.value.lower : bounds.value.upper)}%`,
    transform: `translate(calc(-50% + ${getHandleOffsetPx(kind)}px), -50%)`,
    zIndex: topHandle.value === kind ? 2 : 1,
  });

  const emitValue = (lower: number, upper: number) => {
    const current = bounds.value;
    if (lower === current.lower && upper === current.upper) return;

    emit('update:modelValue', { min: props.range ? lower : props.min, max: upper });
  };

  // Двигает ручку в новое значение. Ручки не меняются местами и не перекрываются.
  const moveHandle = (kind: HandleKind, value: number) => {
    const { lower, upper } = bounds.value;

    if (!props.range) {
      emitValue(props.min, value);
      return;
    }

    if (kind === HandleKind.Lower) {
      emitValue(Math.min(value, upper), upper);
    } else {
      emitValue(lower, Math.max(value, lower));
    }
  };

  // Находит ближайшее к указателю деление шкалы.
  // stepPx — ширина одного деления в px = ширина шкалы / кол-во делений.
  const valueFromClientX = (clientX: number, offsetPx = 0) => {
    const track = trackRef.value;
    if (!track || steps.value <= 0) return props.min;

    const rect = track.getBoundingClientRect();
    const stepPx = rect.width / steps.value;
    if (stepPx <= 0) return props.min;

    const localX = clientX - rect.left - offsetPx;
    const stepIndex = Math.round(localX / stepPx);

    return clamp(props.min + stepIndex);
  };

  const nearestHandle = (value: number): HandleKind => {
    if (!props.range) return HandleKind.Upper;

    const { lower, upper } = bounds.value;
    if (value <= lower) return HandleKind.Lower;
    if (value >= upper) return HandleKind.Upper;

    return value - lower <= upper - value ? HandleKind.Lower : HandleKind.Upper;
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!draggingHandle.value) return;

    const kind = draggingHandle.value;
    moveHandle(kind, valueFromClientX(event.clientX, getHandleOffsetPx(kind)));
  };

  const stopDrag = () => {
    draggingHandle.value = null;
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', stopDrag);
    document.removeEventListener('pointercancel', stopDrag);
  };

  const startDrag = (kind: HandleKind) => {
    if (props.disabled) return;

    draggingHandle.value = kind;
    topHandle.value = kind;
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopDrag);
    document.addEventListener('pointercancel', stopDrag);
  };

  onBeforeUnmount(stopDrag);

  const onHandlePointerDown = (kind: HandleKind, event: PointerEvent) => {
    event.stopPropagation();
    startDrag(kind);
  };

  const onTrackPointerDown = (event: PointerEvent) => {
    if (props.disabled) return;

    const value = valueFromClientX(event.clientX);
    const kind = nearestHandle(value);
    startDrag(kind);
    moveHandle(kind, value);
  };

  const onHandleKeyDown = (kind: HandleKind, event: KeyboardEvent) => {
    if (props.disabled) return;

    const current = kind === HandleKind.Lower ? bounds.value.lower : bounds.value.upper;
    let next = current;

    switch (event.key) {
      case 'ArrowLeft': {
        next = current - 1;
        break;
      }
      case 'ArrowRight': {
        next = current + 1;
        break;
      }
      default: {
        return;
      }
    }

    event.preventDefault();
    topHandle.value = kind;
    moveHandle(kind, clamp(next));
  };
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    :class="['tl-slider', { 'tl-slider_range': range, 'tl-slider_disabled': disabled }]"
    :style="{ '--tl-slider-handle-size': `${HANDLE_SIZE}px` }"
  >
    <div ref="trackRef" class="tl-slider__track" @pointerdown="onTrackPointerDown">
      <div class="tl-slider__fill" :style="fillStyle" />

      <div
        v-if="range"
        class="tl-slider__handle"
        role="slider"
        aria-orientation="horizontal"
        :tabindex="disabled ? -1 : 0"
        :aria-valuemin="min"
        :aria-valuemax="bounds.upper"
        :aria-valuenow="bounds.lower"
        :aria-disabled="disabled"
        :style="getHandleStyle(HandleKind.Lower)"
        @pointerdown="onHandlePointerDown(HandleKind.Lower, $event)"
        @keydown="onHandleKeyDown(HandleKind.Lower, $event)"
      />

      <div
        class="tl-slider__handle"
        role="slider"
        aria-orientation="horizontal"
        :tabindex="disabled ? -1 : 0"
        :aria-valuemin="range ? bounds.lower : min"
        :aria-valuemax="max"
        :aria-valuenow="bounds.upper"
        :aria-disabled="disabled"
        :style="getHandleStyle(HandleKind.Upper)"
        @pointerdown="onHandlePointerDown(HandleKind.Upper, $event)"
        @keydown="onHandleKeyDown(HandleKind.Upper, $event)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tl-slider {
    $root: &;

    height: var(--tl-slider-handle-size);
    position: relative;
    touch-action: none;
    user-select: none;
    width: 100%;

    &__track {
      background-color: var(--color-neutral-20);
      border-radius: var(--rounding-xs);
      cursor: pointer;
      height: 4px;
      left: calc(var(--tl-slider-handle-size) / 2);
      position: absolute;
      right: calc(var(--tl-slider-handle-size) / 2);
      top: 50%;
      transform: translateY(-50%);
    }

    &__fill {
      background-color: var(--color-brand-secondary);
      border-radius: var(--rounding-xs);
      height: 100%;
      position: absolute;
      top: 0;
    }

    &__handle {
      background-color: var(--color-bg-primary);
      border: 1px solid var(--color-neutral-10);
      border-radius: 50%;
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      cursor: grab;
      height: var(--tl-slider-handle-size);
      position: absolute;
      top: 50%;
      width: var(--tl-slider-handle-size);

      &:active {
        cursor: grabbing;
      }

      &:focus {
        outline: none;
      }
    }

    &_range {
      #{$root}__track {
        left: var(--tl-slider-handle-size);
        right: var(--tl-slider-handle-size);
      }

      #{$root}__fill {
        border-radius: 0;
      }
    }

    &_disabled {
      pointer-events: none;

      #{$root}__fill {
        background-color: var(--color-disabled-900);
      }
    }
  }
</style>
